import CASBlock from "@blocks/CASBlock/CASBlock";
import EventBanner from "@blocks/EventBanner/EventBanner";
import FestivalBanner from "@blocks/FestivalBanner/FestivalBanner";
import FestivalWrapper from "@blocks/FestivalBanner/FestivalWrapper";
import { QnFloatButton } from "@components/Layouts/QuickNews/QnButtons";
import { pubPageAdTargeting } from "@config/AdsConstants";
import CatfishAd from "@elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@elements/Advertisement/helpers";
import PrestitialAd from "@elements/Advertisement/PrestitialAd";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import type { GADataProps } from "@elements/GAData/GAData";
import GAData from "@elements/GAData/GAData";
import MetaTags from "@elements/MetaTags/MetaTags";
import { type MetaTagsProps } from "@elements/MetaTags/MetaTags";
import { Footer } from "@footer/Footer";
import { Header } from "@header/Header";
import {
  HEADER_MAIN_MENU,
  HEADER_MOBILE_MAIN_MENU,
} from "@header/HeaderMenuConstants";
import { HeaderMenuList } from "@header/HeaderMenuList";
import {
  CAS_ALL_PAGE_CAMPAIGN_ID,
  FESTIVAL_FLAG,
} from "@helper/getEnvVariables";
import getPageDetails from "@helper/getPageDetails";
import { useNeuronPageViewPush } from "@hooks/useAnalyticsCall/useNeuronPageViewPush";
import useCasScript from "@hooks/useCasScript";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { usePageScroll } from "@hooks/usePageScroll";
import useSendPermutiveIdentify from "@hooks/useSendPermutiveIdentify";
import { AdTargetingType, PageAdTargetValue } from "@typings/Ads.d";
import { ReactElement, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export type Props = Readonly<{
  testId?: string;
  children: ReactNode;
  gaData?: GADataProps;
  metaTagsProps?: MetaTagsProps;
  sectionName?: string;
  mobwebview?: boolean;
  pageAdTargetValue: PageAdTargetValue;
  gsChannels?: string[];
  showNewsletterSignup?: boolean;
  specialBackground?: boolean;
}>;

/*
 * Container to provide a fixed width and horizontal padding around the content. Typically paired
 * with a Layout component to create reusable templates across all pages. Width of child content as
 * follows with each screen size:

  Viewport width >= 1280: Children component width: 1150px
  Viewport width >= 1024: Children component width: 990px
  Viewport width >= 992: Children component width: 960px
  Viewport width >= 768: Children component width: 720px
  Viewport width >= 576: Children component width: 540px
  All other viewport widths: Children component width: 100% - padding 30px

 * @param {ReactElement} children - Any React Element but likely used to wrap a Tamil Murasu module
 * @param {String} testId - Unique string used to target elements when testing
 * @returns {ReactElement} - A wrapped module with a fixed width that resizes on view port resize
 */

export default function MainContainer({
  children,
  testId = "main-container",
  gaData,
  metaTagsProps,
  sectionName,
  mobwebview = false,
  pageAdTargetValue,
  gsChannels,
  showNewsletterSignup = true,
  specialBackground = false,
}: Props): ReactElement {
  const { hasDesktopNav } = getPageDetails(sectionName) || {
    hasDesktopNav: true,
  };

  const { isPrestitialEnabled, isCatfishEnabled } = useGlobalAdSetting();

  useNeuronPageViewPush();
  useSendPermutiveIdentify();
  const { isScriptReady: isCasScriptReady } = useCasScript();
  const isMaanavarMurasuSection = sectionName === "maanavar-murasu";

  sectionName = sectionName == "/" ? "home" : sectionName;
  const pageAdTarget = pubPageAdTargeting[pageAdTargetValue] as AdTargetingType<
    string,
    PageAdTargetValue
  >;

  // sets the initial starting point to 256px, when user scroll the header will stay for a while before it hides.
  const scrollDir = usePageScroll({ initialStart: 256 });

  // Sets quick news reference code.
  const qnRefcode =
    sectionName === "home" ? "home-quick-news" : "listing-quick-news";

  return (
    <>
      <FestivalWrapper
        className={twMerge(
          "",
          (sectionName === "general-election-2025" || specialBackground) &&
            "bg-gray-500",
        )}
      >
        {FESTIVAL_FLAG && <FestivalBanner />}
        {!mobwebview && (
          <Header sectionName={sectionName} scrollDir={scrollDir} />
        )}

        {hasDesktopNav && (
          <div className="hidden lg:block sticky top-0 z-50 bg-white-100 w-full">
            <HeaderMenuList
              testId="desktop-main-links"
              items={HEADER_MAIN_MENU}
              type="main"
              isHamburger={false}
            />
          </div>
        )}
        {!isMaanavarMurasuSection && !mobwebview && (
          <div
            className={twMerge(
              "mobile-menu lg:hidden block z-50 bg-white-100 w-full sticky top-0 transition-all duration-1000 ease-in-out",
              scrollDir == "down"
                ? "top-0 translate-y-0"
                : "top-[52px] translate-y-1",
            )}
          >
            <HeaderMenuList
              testId="mobile-main-links"
              items={HEADER_MOBILE_MAIN_MENU}
              type="mobile-main"
              isHamburger={false}
              sectionName={sectionName}
            />
          </div>
        )}

        {isCasScriptReady && CAS_ALL_PAGE_CAMPAIGN_ID && (
          <CASBlock casId={CAS_ALL_PAGE_CAMPAIGN_ID} />
        )}

        {sectionName != "budget-2026" && (
          <EventBanner
            link="/keywords/budget2026?ref=budget-banner-web"
            title="பட்ஜெட் 2026"
          />
        )}

        {!mobwebview && (
          <AdsLb1
            sectionName={sectionName}
            pageAdTargetType={pageAdTargetValue}
          />
        )}
        {(sectionName === "general-election-2025" || specialBackground) && (
          <div className="w-full h-2.5 bg-gradient-to-r from-pink-701 to-pink-301 mb-5 relative"></div>
        )}

        <QnFloatButton refCode={qnRefcode} />

        <div
          data-testid={testId}
          className={twMerge(
            "xl:max-w-xl w-full mx-auto xxs:px-sm lg:p-0 max-xsm:px-1",
            sectionName === "about-us" &&
              "xl:max-w-full xxs:!px-0 max-xsm:!px-0",
          )}
        >
          <PrestitialAd
            slot={GoogleAdsSlotFactory.prestitial(sectionName)}
            isPrestitialEnabled={isPrestitialEnabled}
            pubAdTargeting={[pageAdTarget]}
          />
          {gaData && <GAData {...gaData} />}
          {metaTagsProps && <MetaTags {...metaTagsProps} />}
          {children}
        </div>
        {!mobwebview && (
          <Footer
            className={sectionName === "about-us" ? "mt-0 lg:mt-500" : ""}
            showNewsletterSignup={showNewsletterSignup}
          />
        )}
        <CatfishAd
          slot={GoogleAdsSlotFactory.catfish(sectionName)}
          isCatfishEnabled={isCatfishEnabled}
          pubAdTargeting={[pageAdTarget]}
          gsChannels={gsChannels}
        />
      </FestivalWrapper>
    </>
  );
}
